<template>
    <Card class="process-instance-table">
        <template #title>Process Instances</template>
        <template #content>
            <DataTable
                :value="instances"
                :loading="loading"
                stripedRows
                paginator
                :rows="10"
                :rowsPerPageOptions="[5, 10, 20, 50]"
                sortField="started_at"
                :sortOrder="-1"
                class="p-datatable-sm"
            >
                <Column
                    field="process_instance_id"
                    header="Instance ID"
                    sortable
                >
                    <template #body="slotProps">
                        <span class="instance-id">
                            {{ slotProps.data.process_instance_id }}
                        </span>
                    </template>
                </Column>
                <Column field="business_key" header="Business Key" sortable>
                    <template #body="slotProps">
                        <span v-if="slotProps.data.business_key">
                            {{ slotProps.data.business_key }}
                        </span>
                        <span v-else class="text-muted">—</span>
                    </template>
                </Column>
                <Column field="status" header="Status" sortable>
                    <template #body="slotProps">
                        <Tag
                            :value="slotProps.data.status"
                            :severity="getStatusSeverity(slotProps.data.status)"
                        />
                    </template>
                </Column>
                <Column field="started_at" header="Started" sortable>
                    <template #body="slotProps">
                        {{ formatDateTime(slotProps.data.started_at) }}
                    </template>
                </Column>
                <Column field="duration_seconds" header="Duration" sortable>
                    <template #body="slotProps">
                        {{ formatDuration(slotProps.data.duration_seconds) }}
                    </template>
                </Column>
                <Column field="total_events" header="Events" sortable>
                    <template #body="slotProps">
                        {{ slotProps.data.total_events }}
                    </template>
                </Column>
                <Column field="error_count" header="Errors" sortable>
                    <template #body="slotProps">
                        <span
                            :class="{
                                'text-danger': slotProps.data.error_count > 0,
                            }"
                        >
                            {{ slotProps.data.error_count }}
                        </span>
                    </template>
                </Column>
                <!-- Compliance Evidence Column
                     Note: compliance_proof_count = number of events with evidence
                     (technical: "proof" = container, can have multiple "evidence" items) -->
                <Column
                    v-if="complianceEnabled"
                    field="compliance_proof_count"
                    header="Evidence"
                    sortable
                >
                    <template #body="slotProps">
                        <a
                            v-if="slotProps.data.compliance_proof_count > 0"
                            href="#"
                            class="evidence-link"
                            @click.prevent="
                                viewComplianceProof(
                                    slotProps.data.process_instance_id,
                                    slotProps.data.business_key
                                )
                            "
                        >
                            {{ slotProps.data.compliance_proof_count }}
                        </a>
                        <span v-else class="text-muted">—</span>
                    </template>
                </Column>
                <Column field="stek_release" header="Release" sortable />
            </DataTable>
        </template>
    </Card>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Tag from "primevue/tag";
import { useRoute, useRouter } from "vue-router";

import type { types } from "@/lib/openapi";

type ProcessInstanceMetrics = types.ProcessInstanceMetrics;

defineOptions({
    name: "ProcessInstanceTable",
});

defineProps<{
    instances: ProcessInstanceMetrics[];
    loading: boolean;
    complianceEnabled?: boolean;
}>();

const router = useRouter();
const route = useRoute();

const viewComplianceProof = (
    processInstanceId: string,
    businessKey: string | null
) => {
    router.push({
        name: "complianceEvidencePage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
        },
        query: {
            process_instance_id: processInstanceId,
            ...(businessKey && { business_key: businessKey }),
        },
    });
};

const getStatusSeverity = (
    status: string
): "success" | "danger" | "info" | "warn" => {
    switch (status) {
        case "completed":
            return "success";
        case "failed":
            return "danger";
        case "running":
            return "info";
        default:
            return "warn";
    }
};

const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString();
};

const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return "—";

    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}m ${secs}s`;
    } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    }
};
</script>

<style scoped lang="scss">
.process-instance-table {
    margin-top: 2rem;

    :deep(.p-datatable-tbody > tr > td) {
        font-size: 0.875rem;
    }

    .instance-id {
        font-size: 0.875rem;
    }

    .text-muted {
        color: var(--text-color-secondary);
        font-size: 0.875rem;
    }

    .text-danger {
        color: var(--red-500);
        font-size: 0.875rem;
    }

    .evidence-link {
        font-size: 0.875rem;
    }
}
</style>

<style lang="scss">
.process-instance-table .evidence-link {
    color: var(--stekz-primary-color) !important;
}
</style>
